import { RequireAtLeastOne } from 'type-fest';

import { Pagination } from './common';

export enum DatasourceStatus {
  FAILED = 'failed',
  PENDING = 'pending',
  INVALID = 'invalid',
  PROCESSING = 'processing',
  AVAILABLE = 'available',
}

export enum DocumentStatus {
  FAILED = 'failed',
  PENDING = 'pending',
  AVAILABLE = 'available',
  INDEXING = 'indexing',
}

export type Datable = { readonly created?: string; readonly updated?: string };

export type CollectionContent = {
  faq: number;
  files: number;
  website: number;
  external_sources: number;
};

export type Collection = {
  account_id: string;
  collection_id: string;
  name: string;
  language: string;
  created: string;
  updated: string;
  model_id: string;
  fragment_count: number;
  last_used_at: string;
  created_by: string;
  content?: CollectionContent;
};

export type Collections = { collections: Collection[] } & Partial<Pagination>;

export type PartialCollection = RequireAtLeastOne<Partial<Collection>, 'collection_id'>;

export enum DatasourceType {
  WEBSITE = 'website',
  ZENDESK_KB = 'zendesk_kb',
  INTERCOM_KB = 'intercom_kb',
  WORKVIVO_KB = 'workvivo',
  GDRIVE = 'gdrive',
  FILES = 'files',
  FAQ = 'faq',
}

type DocumentMetadata = Partial<{
  etag: string;
  created_by: string;
  created_at: string;
  source: string;
  external_id: string;
  preview: string;
}>;

export interface WebsiteConfig {
  seed_urls?: string[];
  single_urls?: string[];
  sitemap_urls?: string[];
  exclude_urls?: string[];
  user_agent?: string;
  cookies?: Record<string, string>;
  auth?: { username: string; password: string };
  use_proxy?: boolean;
  proxy_country?: string; // ISO-3166 alpha-2, uppercase (e.g. 'US')
}

export interface ZendeskConfig {
  subdomain: string;
  access_token: string;
  locales?: string[];
  email: string;
  categories?: string[];
}

export interface IntercomConfig {
  access_token: string;
  admin_id: string;
  admin_name: string;
  page_id: string;
  page_name: string;
}

export interface FileConfig {
  locale: string;
}

export interface WorkvivoConfig {
  client_id: string;
  access_token: string;
  space_ids?: string[];
  pages?: boolean;
  articles?: boolean;
  goals?: boolean;
}

export interface GoogleDrivePickedFile {
  id: string;
  name: string;
}

export interface GoogleDriveConfig {
  /** Write-only: the API masks it on read and keeps the stored token when an update omits it. */
  refresh_token?: string;
  files: GoogleDrivePickedFile[];
}

export type CollectionDocument = {
  datasource_id: string;
  document_id: string;
  collection_id: string;
  name: string;
  enabled: boolean;
  // Type of the document e.g. txt, pdf, etc.
  type: string;
  status: DocumentStatus;
  status_code: string;
  external_url: string | null;
  external_id: string | null;
  file_url: string;
  size_bytes: number;
  statistics: Record<string, unknown>;
  digest: string | null;
  edited_at: string | null;
  metadata: DocumentMetadata;
  created_by: string;
  is_draft: boolean;
  datasource_type: DatasourceType;
} & Datable;

export type Datasource = {
  type: DatasourceType;
  datasource_id: string;
  collection_id: string;
  pending_count: number;
  status: DatasourceStatus;
  name: string;
  enabled: boolean;
  config:
    | ZendeskConfig
    | IntercomConfig
    | WebsiteConfig
    | FileConfig
    | WorkvivoConfig
    | GoogleDriveConfig;

  document_count: number;
  indexing_count: number;
  failed_count: number;

  last_indexed_at?: string;
  created_by?: string;

  is_draft?: boolean;

  last_indexed_details?: IndexingDetails;
} & Datable;

/** Older rows carry bare strings; the indexer has sent coded objects for a while. Both occur. */
export type IndexingNotice = string | ({ code: string } & Record<string, string>);

export type ReportSeverity = 'blocking' | 'advisory' | 'info';

export type ReportActionName =
  | 'allowlist_crawler'
  | 'fix_sitemap'
  | 'consider_excluding'
  | 'review_rendering'
  | 'no_action'
  | 'contact_support'
  | 'update_datasource_config';

export type ReportAction = {
  action: ReportActionName;
  severity: ReportSeverity;
  cause: string;
  affected: number;
  /** A url for a website, a category or space id for a knowledge base. */
  samples?: string[];
  /** Reports stored before the rename carry this instead. Read both. */
  sample_urls?: string[];
};

/**
 * Customer-facing crawl summary. Absent on datasources indexed before it shipped, so every
 * consumer must fall back to the notice arrays.
 */
export type IndexingReport = {
  version: number;
  outcome: 'completed' | 'partial' | 'failed';
  duration_ms: number;
  counts: {
    indexed: number;
    failed: number;
    skipped_by_your_rules: number;
    skipped_known_empty: number;
  };
  rendering: 'static' | 'clientOnly' | null;
  actions: ReportAction[];
};

export type IndexingDetails = {
  start_time: number;
  end_time: number;
  errors: IndexingNotice[];
  recommendations: IndexingNotice[];
  warnings: IndexingNotice[];
  report?: IndexingReport | null;
};

export type Datasources = { datasources: Datasource[] };

export type PartialDatasource = RequireAtLeastOne<Partial<Datasource>, 'datasource_id'>;

export type DocumentErrorCode = 'doc_200' | 'doc_500' | 'doc_601' | 'doc_603' | 'doc_604';

export type Document = {
  name: string;
  type: string;
  file_url: string;
  datasource_id: string;
  document_id: string;
  status: DocumentStatus;
  external_url: string;
  size_bytes: number;
  enabled: boolean;
  digest: string;
  status_code: DocumentErrorCode;
  created_by: string;
  statistics: { fragment?: number; sessions?: number; views?: number };
  is_draft: boolean;
} & Datable;

export type Documents = { documents: Document[] } & Pagination;

export type PartialDocument = RequireAtLeastOne<Partial<CollectionDocument>, 'document_id'>;

// Table-specific type that resolves the type conflict
export interface TableRowData {
  // Unique identifier
  id: string;

  // Display information
  name: string;
  displayType: DatasourceType; // What icon/type to show
  fileType?: string; // Original file type (for documents)

  // Common properties
  status: DocumentStatus | DatasourceStatus;
  enabled: boolean;
  created: string;
  created_by: string;

  // Entity identification
  entityType: 'datasource' | 'document';

  type: string | DatasourceType; // Type of the datasource or document

  // Original data for actions
  originalData: Datasource | CollectionDocument;

  is_draft: boolean;
}

// Adapter functions to convert each type to table format
export const adaptDatasourceForTable = (datasource: Datasource): TableRowData => ({
  id: datasource.datasource_id,
  name: datasource.name,
  displayType: datasource.type,
  fileType: undefined,
  status: datasource.status,
  enabled: datasource.enabled,
  created: datasource.created || '',
  created_by: datasource.created_by || '',
  entityType: 'datasource',
  originalData: datasource,
  is_draft: datasource.is_draft || false,
  type: datasource.type, // Use the type from the datasource
});

export const adaptDocumentForTable = (document: CollectionDocument): TableRowData => ({
  id: document.document_id,
  name: document.name,
  displayType: document.datasource_type,
  fileType: document.type,
  status: document.status,
  enabled: document.enabled,
  created: document.created || '',
  created_by: document.created_by,
  entityType: 'document',
  originalData: document,
  is_draft: document.is_draft || false,
  type: document.type, // Use the type from the document
});

export interface CollectionsState {
  isPublishModalOpen: boolean;
  isDeleteDatasourceModalOpen: boolean;
  isDeleteCollectionModalOpen: boolean;
  isDeleteDocumentModalOpen: boolean;
  isDeleteDocumentsModalOpen: boolean;
  selectedDocumentsForDeletion: CollectionDocument[];
  // The delete modals own their target rather than reading the session selection, so opening one
  // from a row menu cannot retarget a right panel that is already open on another row.
  deleteDatasourceTargetId: string | null;
  deleteDocumentTarget: { datasource_id: string; document_id: string } | null;
}
